import {useCallback, useEffect, useMemo, useState} from 'react';

import {useDeepMemo, useDispatch} from '../../../../state/hooks';
import {useHistoryKeyInfoQueryVars} from '../../../../state/runs/hooks';
import * as PanelBankConfigActions from '../../../../state/views/panelBankConfig/actions';
import * as PanelBankSectionConfigActions from '../../../../state/views/panelBankSectionConfig/actions';
import {useDebounceState} from '../../../../util/hooks';
import {getDefaultPanelConfig} from '../../../../util/panelbank';
import {useKeyInfoQueryContext} from '../../../MultiRunWorkspace/KeyInfoQueryContext';
import {AddPanel} from '../../../PanelBank/types';
import {PAGE_SIZE} from '../constants';
import {QuickAddKeys} from '../PanelsQuickAddContext';
import {useQuickAddKeysPaginated} from './useQuickAddKeys';

interface UsePaginatedQuickAddProps {
  initialSearchStr: string;
  isGlobalQuickAdd: boolean;
  targetSectionRef: any;
  panelBankConfigRef: any;
  sortAlphabetically: boolean;
  trackQuickAddBound: (event: any) => void;
}

interface UsePaginatedQuickAddResult {
  searchStr: string;
  dSearchStr: string;
  setSearchStr: (value: string) => void;
  regexError: string | null;
  searchedKeys: ReturnType<typeof useQuickAddKeysPaginated>['searchedKeys'];
  numPanelsByKey: ReturnType<typeof useQuickAddKeysPaginated>['numPanelsByKey'];
  displayedKeys: QuickAddKeys;
  displayedKeysWithNoPanels: QuickAddKeys;
  loadMore: () => void;
  onAddOne: (key: string, spec: AddPanel['spec']) => void;
  onBulkAdd: () => void;
  cursor: string | null;
}

export const usePaginatedQuickAdd = ({
  initialSearchStr,
  isGlobalQuickAdd,
  targetSectionRef,
  panelBankConfigRef,
  sortAlphabetically,
  trackQuickAddBound,
}: UsePaginatedQuickAddProps): UsePaginatedQuickAddResult => {
  const dispatch = useDispatch();

  // Get run set filters to watch for changes
  const {runSetRefs, customRunNamesRef} = useKeyInfoQueryContext();
  const {filters: rawFilters} = useHistoryKeyInfoQueryVars(
    runSetRefs,
    customRunNamesRef
  );
  const filters = useDeepMemo(rawFilters);

  // Search state
  const [searchStr, dSearchStr, setSearchStr] = useDebounceState(
    initialSearchStr,
    200
  );
  useEffect(
    () => setSearchStr(initialSearchStr),
    [initialSearchStr, setSearchStr]
  );

  // Pagination state
  const [cursor, setCursor] = useState<string | null>(null);
  const [regexError, setRegexError] = useState<string | null>(null);
  const [allKeys, setAllKeys] = useState<Map<string, AddPanel['spec']>>(
    new Map()
  );
  const [allKeysWithNoPanels, setAllKeysWithNoPanels] = useState<
    Map<string, AddPanel['spec']>
  >(new Map());

  // Validate regex before passing to hook
  const validatedSearchStr = useMemo(() => {
    if (!dSearchStr) {
      setRegexError(null);
      return '';
    }
    try {
      new RegExp(dSearchStr);
      setRegexError(null);
      return dSearchStr;
    } catch (e) {
      setRegexError(`Invalid regex: "${dSearchStr}"`);
      return ''; // Pass empty string to prevent backend error
    }
  }, [dSearchStr]);

  const {searchedKeys, numPanelsByKey} = useQuickAddKeysPaginated(
    validatedSearchStr,
    cursor,
    PAGE_SIZE
  );

  // Reset accumulated keys when quick add search or run set filters change
  useEffect(() => {
    setAllKeys(new Map());
    setAllKeysWithNoPanels(new Map());
    setCursor(null);
  }, [validatedSearchStr, filters]);

  // Accumulate keys as we load more pages
  useEffect(() => {
    if (searchedKeys.keys.length > 0) {
      setAllKeys(prev => {
        const newMap = new Map(prev);
        let hasNewKeys = false;

        for (const [key, spec] of searchedKeys.keys) {
          if (!newMap.has(key)) {
            newMap.set(key, spec);
            hasNewKeys = true;
          }
        }

        return hasNewKeys ? newMap : prev;
      });

      // Also accumulate keysWithNoPanels
      setAllKeysWithNoPanels(prev => {
        const newMap = new Map(prev);
        let hasNewKeys = false;

        for (const [key, spec] of searchedKeys.keys) {
          if (!newMap.has(key) && !numPanelsByKey.global.get(key)) {
            newMap.set(key, spec);
            hasNewKeys = true;
          }
        }

        return hasNewKeys ? newMap : prev;
      });
    }
  }, [searchedKeys.keys, numPanelsByKey]);

  const loadMore = useCallback(() => {
    if (searchedKeys.hasNextPage && searchedKeys.nextCursor) {
      setCursor(searchedKeys.nextCursor);
    }
  }, [searchedKeys.hasNextPage, searchedKeys.nextCursor]);

  const onAddOne = useCallback(
    (key: string, spec: AddPanel['spec']) => {
      trackQuickAddBound({action: 'Add one clicked'});
      if (targetSectionRef) {
        dispatch(
          PanelBankSectionConfigActions.addPanelByConfig(
            targetSectionRef,
            getDefaultPanelConfig(key, spec),
            sortAlphabetically
          )
        );
      } else {
        dispatch(
          PanelBankConfigActions.addPanelsBySpec(
            panelBankConfigRef,
            [{key, spec}],
            sortAlphabetically
          )
        );
      }

      // Update allKeysWithNoPanels to remove the key that now has a panel
      setAllKeysWithNoPanels(prev => {
        if (prev.has(key)) {
          const newMap = new Map(prev);
          newMap.delete(key);
          return newMap;
        }
        return prev;
      });
    },
    [
      dispatch,
      panelBankConfigRef,
      sortAlphabetically,
      targetSectionRef,
      trackQuickAddBound,
    ]
  );

  const onBulkAdd = useCallback(() => {
    // Clear all keys that were added in bulk
    setAllKeysWithNoPanels(new Map());
  }, []);

  // Display logic: Show first page results directly from the API (searchedKeys.keys)
  // when cursor is null (initial load). Show accumulated results (allKeys) when
  // loading additional pages to maintain previously loaded results.
  const displayedKeys: QuickAddKeys =
    cursor === null ? searchedKeys.keys : Array.from(allKeys.entries());

  // Use accumulated keysWithNoPanels when loading additional pages
  // For the first page, calculate keysWithNoPanels from searchedKeys
  const displayedKeysWithNoPanels: QuickAddKeys =
    cursor === null
      ? searchedKeys.keys.filter(([key]) => !numPanelsByKey.global.get(key))
      : Array.from(allKeysWithNoPanels.entries());

  return {
    searchStr,
    dSearchStr,
    setSearchStr,
    regexError,
    searchedKeys,
    numPanelsByKey,
    displayedKeys,
    displayedKeysWithNoPanels,
    loadMore,
    onAddOne,
    onBulkAdd,
    cursor,
  };
};
